import { Controller } from "@hotwired/stimulus"

// Collapsible sidebar group. Toggles the child list, persists the
// open/closed choice per group in localStorage, and force-opens when the
// user is on one of the group's child pages (active child wins over
// persisted state, so the page you're on is never hidden).
//
// Every mount today is a top-level nav SECTION (.sidebar-section). The
// name is historical: this started as the "AI Tools" sub-dropdown, and
// the generic `sidebar-group-*` data attributes are what the sections
// still bind to.
//
// State lives in the wrapper's `data-expanded` attribute, which the CSS
// keys off (see app.tailwind.css `.sidebar-section[data-expanded]`). The
// server pre-renders `data-expanded="true"`, so nothing flashes.
export default class extends Controller {
  // defaultOpen: what to do when the user has never toggled this group.
  // Every consumer today is a top-level nav SECTION, and sections want OPEN:
  // defaulting them closed would render three headings and no links on a
  // first visit. Absent => false, kept so a collapsed-by-default group
  // still works if one is reintroduced.
  static values = { key: String, hasActiveChild: Boolean, defaultOpen: Boolean }

  connect() {
    if (this.hasActiveChildValue) {
      // On a child page: always open, ignore persisted state.
      this.setExpanded(true)
      return
    }
    // Otherwise apply the persisted preference, falling back to whatever this
    // group declares as its default (see defaultOpenValue above).
    let stored = null
    try {
      stored = localStorage.getItem(this.storageKey)
    } catch (_e) {
      // localStorage blocked — fall back to the declared default.
    }
    this.setExpanded(stored === null ? this.defaultOpenValue : stored === "true")
  }

  toggle() {
    const next = this.element.dataset.expanded !== "true"
    this.setExpanded(next)
    try {
      localStorage.setItem(this.storageKey, next ? "true" : "false")
    } catch (_e) {
      // Non-fatal — toggle still works for this session.
    }
  }

  setExpanded(expanded) {
    this.element.dataset.expanded = expanded ? "true" : "false"
    // `:scope >` matters: it kept a nav SECTION from writing its own
    // aria-expanded onto the trigger of a group nested inside it. Nothing is
    // nested today, but the scope is the correct query either way — keep it so
    // reintroducing a sub-dropdown doesn't resurrect that bug.
    const trigger = this.element.querySelector(":scope > .sidebar-group-trigger")
    if (trigger) trigger.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  get storageKey() {
    return `sidebar-group-${this.keyValue}`
  }
}
